/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.feature.sessions

import androidx.lifecycle.viewModelScope
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import studio.oddpods.cauce.core.base.ui.BaseViewModel
import studio.oddpods.cauce.core.data.repository.cauce.PatientRepository
import studio.oddpods.cauce.core.data.repository.cauce.RecurringScheduleRepository
import studio.oddpods.cauce.core.formatter.CurrencyFormatter
import studio.oddpods.cauce.core.formatter.DateFormatter
import studio.oddpods.cauce.core.model.patient.Patient
import studio.oddpods.cauce.core.model.session.CreateRecurringScheduleRequest
import studio.oddpods.cauce.core.model.session.RecurrenceFrequency
import studio.oddpods.cauce.core.ui.UiText
import studio.oddpods.cauce.core.ui.toUiText
import studio.oddpods.cauce.feature.sessions.generated.resources.Res
import studio.oddpods.cauce.feature.sessions.generated.resources.recurring_schedule_error_fee_invalid
import studio.oddpods.cauce.feature.sessions.generated.resources.recurring_schedule_error_interval_invalid
import studio.oddpods.cauce.feature.sessions.generated.resources.recurring_schedule_error_load_patients
import studio.oddpods.cauce.feature.sessions.generated.resources.recurring_schedule_error_patient_required
import studio.oddpods.cauce.feature.sessions.generated.resources.recurring_schedule_error_save_failed
import kotlin.time.Clock

data class CreateRecurringScheduleState(
    val patients: List<Patient> = emptyList(),
    val selectedPatientId: String? = null,
    val frequency: RecurrenceFrequency = RecurrenceFrequency.WEEKLY,
    val anchorDate: LocalDate = LocalDate(1970, 1, 1),
    val feeInput: String = "",
    val intervalDaysInput: String = "",
    val isSaving: Boolean = false,
    val isLoadingPatients: Boolean = true,
    val error: UiText? = null,
)

sealed class CreateRecurringScheduleEvent {
    data object SaveSuccess : CreateRecurringScheduleEvent()
}

sealed class CreateRecurringScheduleAction {
    data class PatientSelected(val patientId: String) : CreateRecurringScheduleAction()
    data class FrequencyChanged(val frequency: RecurrenceFrequency) : CreateRecurringScheduleAction()
    data class AnchorDateChanged(val date: LocalDate) : CreateRecurringScheduleAction()
    data class FeeChanged(val fee: String) : CreateRecurringScheduleAction()
    data class IntervalDaysChanged(val days: String) : CreateRecurringScheduleAction()
    data object SaveClicked : CreateRecurringScheduleAction()
}

class CreateRecurringScheduleViewModel(
    clock: Clock = Clock.System,
    private val recurringScheduleRepository: RecurringScheduleRepository,
    private val patientRepository: PatientRepository,
    private val currencyFormatter: CurrencyFormatter,
    val dateFormatter: DateFormatter,
) : BaseViewModel<CreateRecurringScheduleState, CreateRecurringScheduleEvent, CreateRecurringScheduleAction>(
    CreateRecurringScheduleState(),
) {

    init {
        val today = clock.now().toLocalDateTime(TimeZone.currentSystemDefault()).date
        mutableStateFlow.value = state.copy(anchorDate = today)
        loadPatients()
    }

    override fun handleAction(action: CreateRecurringScheduleAction) {
        when (action) {
            is CreateRecurringScheduleAction.PatientSelected -> {
                val patient = state.patients.find { it.id == action.patientId }
                mutableStateFlow.value = state.copy(
                    selectedPatientId = action.patientId,
                    feeInput = if (patient != null) (patient.defaultFee / 100).toString() else state.feeInput,
                    error = null,
                )
            }
            is CreateRecurringScheduleAction.FrequencyChanged ->
                mutableStateFlow.value = state.copy(frequency = action.frequency, error = null)
            is CreateRecurringScheduleAction.AnchorDateChanged ->
                mutableStateFlow.value = state.copy(
                    anchorDate = action.date,
                    error = null,
                )
            is CreateRecurringScheduleAction.FeeChanged ->
                mutableStateFlow.value = state.copy(feeInput = action.fee, error = null)
            is CreateRecurringScheduleAction.IntervalDaysChanged ->
                mutableStateFlow.value = state.copy(intervalDaysInput = action.days, error = null)
            CreateRecurringScheduleAction.SaveClicked -> handleSave()
        }
    }

    private fun loadPatients() {
        viewModelScope.launch {
            patientRepository.getPatients()
                .catch { e ->
                    mutableStateFlow.value = state.copy(
                        isLoadingPatients = false,
                        error = e.toUiText(Res.string.recurring_schedule_error_load_patients),
                    )
                }
                .collect { patients ->
                    mutableStateFlow.value = state.copy(patients = patients, isLoadingPatients = false)
                }
        }
    }

    private fun handleSave() {
        val patientId = state.selectedPatientId
        val feeCentavos = currencyFormatter.parse(state.feeInput)
        val customIntervalDays = state.intervalDaysInput.toIntOrNull()?.takeIf { it > 0 }
        val validationError = when {
            patientId == null ->
                UiText.Resource(Res.string.recurring_schedule_error_patient_required)
            feeCentavos == null || feeCentavos <= 0 ->
                UiText.Resource(Res.string.recurring_schedule_error_fee_invalid)
            state.frequency == RecurrenceFrequency.CUSTOM && customIntervalDays == null ->
                UiText.Resource(Res.string.recurring_schedule_error_interval_invalid)
            else -> null
        }
        if (validationError != null) {
            mutableStateFlow.value = state.copy(error = validationError)
            return
        }
        val intervalDays = if (state.frequency == RecurrenceFrequency.CUSTOM) customIntervalDays else null

        mutableStateFlow.value = state.copy(isSaving = true, error = null)
        viewModelScope.launch {
            recurringScheduleRepository.createSchedule(
                CreateRecurringScheduleRequest(
                    patientId = patientId!!,
                    frequency = state.frequency,
                    intervalDays = intervalDays,
                    dayOfWeek = state.anchorDate.dayOfWeek.ordinal + 1,
                    fee = feeCentavos!!,
                    anchorDate = state.anchorDate.toString(),
                ),
            )
                .onSuccess {
                    mutableStateFlow.value = state.copy(isSaving = false)
                    sendEvent(CreateRecurringScheduleEvent.SaveSuccess)
                }
                .onFailure { e ->
                    mutableStateFlow.value = state.copy(
                        isSaving = false,
                        error = e.toUiText(Res.string.recurring_schedule_error_save_failed),
                    )
                }
        }
    }
}
